/**
 * Servicio de cola de sincronización para operaciones offline
 *
 * Maneja el almacenamiento y sincronización de operaciones realizadas
 * cuando no hay conexión a Internet.
 */

const SYNC_QUEUE_KEY = 'offline_sync_queue';

/**
 * Estructura de una operación en cola:
 * {
 *   id: string,
 *   type: 'complete_order' | 'unmark_order' | 'create_incident',
 *   payload: any,
 *   timestamp: number,
 *   retries: number
 * }
 */

class SyncQueue {
  constructor() {
    this.queue = this.loadQueue();
    this.listeners = [];
  }

  /**
   * Cargar la cola desde localStorage
   */
  loadQueue() {
    try {
      const stored = localStorage.getItem(SYNC_QUEUE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error al cargar la cola de sincronización:', error);
      return [];
    }
  }

  /**
   * Guardar la cola en localStorage
   */
  saveQueue() {
    try {
      localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(this.queue));
      this.notifyListeners();
    } catch (error) {
      console.error('Error al guardar la cola de sincronización:', error);
    }
  }

  /**
   * Agregar una operación a la cola
   */
  add(type, payload) {
    const operation = {
      id: `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      payload,
      timestamp: Date.now(),
      retries: 0
    };

    this.queue.push(operation);
    this.saveQueue();

    console.log('📝 Operación añadida a la cola:', operation);
    return operation.id;
  }

  /**
   * Obtener todas las operaciones pendientes
   */
  getAll() {
    return [...this.queue];
  }

  /**
   * Obtener el número de operaciones pendientes
   */
  getCount() {
    return this.queue.length;
  }

  /**
   * Remover una operación de la cola por ID
   */
  remove(id) {
    const index = this.queue.findIndex(op => op.id === id);
    if (index !== -1) {
      const removed = this.queue.splice(index, 1)[0];
      this.saveQueue();
      console.log('✅ Operación sincronizada y removida:', removed);
      return removed;
    }
    return null;
  }

  /**
   * Incrementar el contador de reintentos de una operación
   */
  incrementRetries(id) {
    const operation = this.queue.find(op => op.id === id);
    if (operation) {
      operation.retries++;
      this.saveQueue();
      return operation;
    }
    return null;
  }

  /**
   * Limpiar todas las operaciones
   */
  clear() {
    this.queue = [];
    this.saveQueue();
    console.log('🗑️ Cola de sincronización limpiada');
  }

  /**
   * Remover operaciones que han fallado demasiadas veces
   */
  removeFailedOperations(maxRetries = 5) {
    const failed = this.queue.filter(op => op.retries >= maxRetries);
    this.queue = this.queue.filter(op => op.retries < maxRetries);

    if (failed.length > 0) {
      this.saveQueue();
      console.warn('⚠️ Operaciones removidas por exceso de reintentos:', failed);
    }

    return failed;
  }

  /**
   * Suscribirse a cambios en la cola
   */
  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Notificar a todos los listeners
   */
  notifyListeners() {
    this.listeners.forEach(listener => {
      try {
        listener(this.queue);
      } catch (error) {
        console.error('Error al notificar listener:', error);
      }
    });
  }

  /**
   * Obtener estadísticas de la cola
   */
  getStats() {
    const byType = {};
    this.queue.forEach(op => {
      byType[op.type] = (byType[op.type] || 0) + 1;
    });

    return {
      total: this.queue.length,
      byType,
      oldestTimestamp: this.queue.length > 0
        ? Math.min(...this.queue.map(op => op.timestamp))
        : null
    };
  }
}

// Exportar una instancia singleton
export const syncQueue = new SyncQueue();

// También exportar la clase por si se necesita crear instancias adicionales
export default SyncQueue;
